import { useMemo } from "react";
import { OrderWithRelations } from "@/components/schedule/types";
import { Tractor, Location } from "@/types/tms";
import { getResourceLocationAtTime } from "@/lib/location-utils";

/**
 * Calculates the current location of a tractor based on its order history.
 * - If no orders or all orders are in the future, returns home base city
 * - If there are completed orders, returns the most recent delivery location
 * - During an active order, returns the ship_from location
 */
export function useTractorCurrentLocation(
  tractor: Tractor & { home_base: Location | null },
  orders: OrderWithRelations[],
  referenceTime: Date = new Date()
): string | null {
  return useMemo(() => {
    if (!orders || orders.length === 0) {
      return tractor.home_base?.city || null;
    }

    // Sort orders by ready_time
    const sortedOrders = [...orders].sort(
      (a, b) => new Date(a.ready_time).getTime() - new Date(b.ready_time).getTime()
    );

    let currentLocation = tractor.home_base?.city || null;

    for (const order of sortedOrders) {
      const orderStart = new Date(order.ready_time);
      const orderEnd = new Date(order.requested_delivery_time);

      // If the order hasn't started yet, we're still at the previous location
      if (orderStart > referenceTime) {
        break;
      }

      // If we're during the order, we're in transit
      if (orderStart <= referenceTime && orderEnd > referenceTime) {
        // Could return null for "in transit" but for work order purposes,
        // we'll use the ship_from location as the starting point
        currentLocation = order.ship_from_city;
        break;
      }

      // If the order has completed, we're at the delivery location
      if (orderEnd <= referenceTime) {
        currentLocation = order.ship_to_city;
      }
    }

    return currentLocation;
  }, [tractor, orders, referenceTime]);
}

/**
 * Gets the current location for a tractor at a specific point in time
 * Uses the shared getResourceLocationAtTime utility
 */
export function getTractorLocationAtTime(
  tractor: Tractor & { home_base: Location | null },
  orders: OrderWithRelations[],
  atTime: Date
): string | null {
  if (!orders || orders.length === 0) {
    return tractor.home_base?.city || null;
  }

  // Sort orders by ready_time for the utility function
  const sortedOrders = [...orders].sort(
    (a, b) => new Date(a.ready_time).getTime() - new Date(b.ready_time).getTime()
  );

  return getResourceLocationAtTime(
    tractor.home_base?.city || null,
    sortedOrders,
    atTime
  );
}
